import { LitElement, css, html, nothing } from '@lit'
import { addToCart, t } from '@nova/utils'
import styles from '@nova/styles'
import { swiperStyles } from '@vendor/swiper-styles'

class ProductComplementaryProductsList extends LitElement {
  static properties = {
    recommendations: {
      type: Array,
    },
    title: {
      type: String,
    },
    addToCartErrorId: {
      type: Number,
      state: true,
    },
  }

  constructor() {
    super()
    this.recommendations = []
    this.title = ''
    this._recommendationsSyncKey = ''
    this.addToCartErrorId = null
  }

  disconnectedCallback() {
    this._destroyCarousel()
    super.disconnectedCallback()
  }

  updated(changedProperties) {
    if (changedProperties.has('recommendations')) {
      const syncKey = this.recommendations.map((recommendation) => recommendation.id).join(',')
      if (syncKey === this._recommendationsSyncKey) return

      this._recommendationsSyncKey = syncKey
      this._syncCarousel()
    }
  }

  _destroyCarousel = () => {
    if (this.$carousel && typeof this.$carousel.destroy === 'function') {
      this.$carousel.destroy(true, true)
    }
    this.$carousel = null
  }

  _syncCarousel = () => {
    this._destroyCarousel()

    if (this.recommendations.length <= 1) return

    const carouselEl = this.shadowRoot?.querySelector('.js-mini-cart-recommendations-carousel')
    if (carouselEl) {
      this._initializeCarousel(carouselEl)
    }
  }

  _initializeCarousel = (carouselEl) => {
    this.$carousel = new Swiper(carouselEl, {
      init: false,
      spaceBetween: 5,
      slidesPerView: 1.2,
      preventClicks: false,
      preventClicksPropagation: false,
      slidesOffsetAfter: 15,
      slidesOffsetBefore: 15,
      navigation: {
        nextEl: this.shadowRoot.querySelector('.mini-cart-recommendations__carousel-next'),
        prevEl: this.shadowRoot.querySelector('.mini-cart-recommendations__carousel-prev'),
      },
      pagination: {
        el: carouselEl.querySelector('.swiper-pagination'),
        type: 'progressbar',
      },
      breakpoints: {
        768: {
          slidesPerView: 1.8,
          spaceBetween: 5,
          slidesOffsetAfter: 15,
          slidesOffsetBefore: 15,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 9.5,
          slidesOffsetAfter: 0,
          slidesOffsetBefore: 0,
        }
      },
      keyboard: {
        enabled: true,
      },
    })
    this.$carousel.init()
    this.$carousel.enable()
  }

  _renderRecommendationCard = (recommendation) => {
    const swatchCount = recommendation.swatch_values?.length ?? 0
    const [productTitle] = recommendation.title.split(' - ')
    const chooseOptionsLabel = `${t('cart.general.choose_options', 'Choose options for')} ${productTitle}`
    const addToCartLabel = `${t('products.product.add_to_cart', 'Add to cart')} ${productTitle}`

    return html`
      <div class="mini-cart-recommendation">
        <div class="mini-cart-recommendation__image">
          <img src="${recommendation.image}" alt="${recommendation.title}">
          ${recommendation.badge_data ? html`
            <p
              class="mini-cart-recommendation__badge-data"
              style="
                --badge-background-color: ${recommendation.badge_data.colour};
                --badge-font-color: ${recommendation.badge_data.font_colour};
              ">
              ${recommendation.badge_data.label}
            </p>
          ` : nothing}
        </div>
        <div class="mini-cart-recommendation__content">
          <div class="mini-cart-recommendation__content-top">
            <a href="${recommendation.url}" class="mini-cart-recommendation__title-link">
              <h6 class="mini-cart-recommendation__title">${productTitle}</h6>
            </a>
            <p class="mini-cart-recommendation__active-swatch-value">
              ${recommendation.active_swatch_value}
              ${swatchCount > 1 ? html`
                <a href="${recommendation.url}" class="mini-cart-recommendation__swatch-values">+${swatchCount - 1} Colors</a>`
                : nothing}
            </p>
          </div>
          <div class="mini-cart-recommendation__price-container">
            <span class="mini-cart-recommendation__price-value">${recommendation.price}</span>
            ${recommendation.variants_size > 1 ? html`
              <a href="${recommendation.url}" class="mini-cart-recommendation__add-to-cart" aria-label="${chooseOptionsLabel}">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M7 0V14" stroke="black"/>
                  <path d="M0 7L14 7" stroke="black"/>
                </svg>
              </a>
            ` : html`
              <button type="button" @click="${() => this._handleAddToCart(recommendation)}" class="mini-cart-recommendation__add-to-cart" aria-label="${addToCartLabel}">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M7 0V14" stroke="black"/>
                  <path d="M0 7L14 7" stroke="black"/>
                </svg>
              </button>
            `}
          </div>
          ${this.addToCartErrorId === recommendation.id ? html`
            <p class="mini-cart-recommendation__error" role="alert">
              ${t('cart.general.cart_error', 'There was an error while updating your cart. Please try again.')}
            </p>
          ` : nothing}
        </div>
      </div>
    `
  }

  _handleAddToCart = async (recommendation) => {
    this.addToCartErrorId = null

    try {
      await addToCart({ id: recommendation.id, quantity: 1 }, false)
      document.dispatchEvent(
        new CustomEvent('nova:cart:refresh', {
          bubbles: true,
          composed: true,
          detail: { scrollToTop: true },
        })
      )

      document.dispatchEvent(
        new CustomEvent('nova:cart:open', {
          bubbles: true,
          composed: true
        })
      )
    } catch {
      this.addToCartErrorId = recommendation.id
    }
  }

  render() {
    const previousSlideLabel = t('accessibility.previous_slide', 'Slide left')
    const nextSlideLabel = t('accessibility.next_slide', 'Slide right')
    const paginationLabel = t('general.slider.name', 'Slider')

    return html`
      <div class="mini-cart-recommendations">
        ${this.title || this.recommendations.length > 1 ? html`
          <div class="mini-cart-recommendations__header">
            ${this.title ? html`<h3 class="mini-cart-recommendations__title">${this.title}</h3>` : html`<span class="mini-cart-recommendations__title"></span>`}
            ${this.recommendations.length > 1 ? html`
              <div class="mini-cart-recommendations__carousel-navigation">
                <button type="button" class="carousel--icon-container mini-cart-recommendations__carousel-prev" aria-label="${previousSlideLabel}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="14" viewBox="0 0 20 14" fill="none" aria-hidden="true">
                    <path d="M7.12382 13.1869L0.707153 6.77024L7.12382 0.353577M0.707153 6.77024L19.9572 6.77024" stroke="black"/>
                  </svg>
                </button>
                <button type="button" class="carousel--icon-container mini-cart-recommendations__carousel-next" aria-label="${nextSlideLabel}">
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true">
                    <path d="M13.7083 4.08336L20.125 10.5L13.7083 16.9167M20.125 10.5L0.875001 10.5" stroke="black"/>
                  </svg>
                </button>
              </div>
            ` : nothing}
          </div>
        ` : nothing}
        ${this.recommendations.length > 1 ? html`
          <div class="mini-cart-recommendations__carousel js-mini-cart-recommendations-carousel">
            <div class="swiper-wrapper">
              ${this.recommendations.map(recommendation => html`
                <div class="swiper-slide">
                  ${this._renderRecommendationCard(recommendation)}
                </div>
              `)}
            </div>
            <div class="swiper-pagination" role="navigation" aria-label="${paginationLabel}"></div>
          </div>
        ` : html`
          <div class="mini-cart-recommendations__single">
            ${this._renderRecommendationCard(this.recommendations[0])}
          </div>
        `}
      </div>
    `
  }

  static styles = [
    swiperStyles,
    styles.flex,
    css`
      :host {
        max-width: var(--size);
        display: contents;
      }
      .mini-cart-recommendations {
        width: 100%;
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-sm);
        padding-block: var(--nova-space-md);
        margin-bottom: 1px;
        @media screen and (min-width: 1024px) {
          padding-block: var(--nova-space-xs);
          gap: var(--nova-space-xsm);
          margin-block-start: var(--nova-space-md);
        }
      }
      .mini-cart-recommendations__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--nova-space-sm);
        @media screen and (min-width: 1024px) {
          gap: var(--nova-space-xsm);
        }
      }
      .mini-cart-recommendations__title {
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        text-transform: var(--nova-body-text-transform);
        font-size: 12px;
        font-weight: 300;
        line-height: 110%;
        color: var(--nova-color-black);
        margin: 0;
        @media screen and (min-width: 1024px) {
          font-size: 14px;
        }
      }
      .mini-cart-recommendation {
        background-color: var(--nova-color-white);
        padding: var(--nova-space-xs) var(--nova-space-sm) var(--nova-space-xs) var(--nova-space-xs);
        gap: var(--nova-space-md);
        display: flex;
        align-items: center;
        border: 1px solid var(--nova-color-grey-200);
        @media screen and (min-width: 1024px) {
          gap: var(--nova-space-xsm);
          flex-direction: column;
          padding: var(--nova-space-2xs);
          height: 100%;
          height: stretch;
        }
      }
      .mini-cart-recommendations__single .mini-cart-recommendation {
        @media screen and (min-width: 1024px) {
          padding: var(--nova-space-xs) var(--nova-space-xsm) var(--nova-space-xs) var(--nova-space-xs);
          gap: var(--nova-space-sm);
          flex-direction: row;
          height: 100%;
        }
      }
      .mini-cart-recommendation__image {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 120px;
        height: 120px;
        position: relative;
        @media screen and (min-width: 1024px) {
          width: 100%;
          height: auto;
        }
      }
      .mini-cart-recommendations__single .mini-cart-recommendation__image {
        @media screen and (min-width: 1024px) {
          width: 120px;
          height: 120px;
        }
      }
      .mini-cart-recommendation__image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .mini-cart-recommendation__content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-xs);
        position: relative;
        @media screen and (min-width: 1024px) {
          padding: 0 var(--nova-space-2xs) var(--nova-space-2xs);
          width: 100%;
          box-sizing: border-box;
          justify-content: space-between;
        }
      }
      .mini-cart-recommendations__single .mini-cart-recommendation__content {
        @media screen and (min-width: 1024px) {
          padding: 0;
        }
      }
      .mini-cart-recommendation__content-top {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-xs);
        flex: 1;
        justify-content: space-between;
      }
      .mini-cart-recommendation__title-link {
        text-decoration: none;
      }
      .mini-cart-recommendation__title {
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        text-transform: var(--nova-body-text-transform);
        font-size: 14px;
        font-weight: 300;
        line-height: 120%;
        color: var(--nova-color-grey-600);
        margin: 0;
      }
      .mini-cart-recommendation__price-container {
        display: flex;
        align-items: center;
        gap: var(--nova-space-2xs);
        justify-content: space-between;
      }
      .mini-cart-recommendation__price-value {
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        text-transform: var(--nova-body-text-transform);
        color: var(--nova-color-grey-600);
        font-size: 14px;
        font-weight: 300;
        line-height: 120%;
      }
      .mini-cart-recommendation__badge-data {
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        color: var(--nova-color-white);
        position: absolute;
        top: var(--nova-space-xs);
        left: var(--nova-space-xs);
        background-color: var(--badge-background-color);
        color: var(--badge-font-color);
        padding: var(--nova-space-2xs) var(--nova-space-xs);
        border-radius: 1000px;
        font-size: 12px;
        font-weight: 300;
        line-height: 110%;
        pointer-events: none;
        margin: 0;
      }
      .mini-cart-recommendation__active-swatch-value,
      .mini-cart-recommendation__swatch-values {
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        text-transform: var(--nova-body-text-transform);
        color: var(--nova-color-grey-400);
        font-size: 12px;
        font-weight: 400;
        line-height: 120%;
        cursor: pointer;
      }
      .mini-cart-recommendation__active-swatch-value {
        display: flex;
        align-items: center;
        gap: var(--nova-space-2xs);
        margin: 0;
      }
      .mini-cart-recommendation__swatch-values {
        text-decoration: underline;
      }
      .mini-cart-recommendations__carousel {
        overflow: hidden;
        position: relative;
        padding-bottom: 18px;
        margin-inline: calc(0px - var(--nova-space-sm));
        @media screen and (min-width: 1024px) {
          margin-inline: 0;
        }
      }
      .mini-cart-recommendations__carousel .swiper-slide {
        box-sizing: border-box;
        @media screen and (min-width: 1024px) {
          height: auto;
        }
      }
      .mini-cart-recommendations__carousel .swiper-pagination {
        top: auto;
        bottom: 0;
        left: 15px;
        right: 15px;
        width: calc(100% - 30px);
        background: var(--nova-color-grey-200);
        border-radius: 0;
        overflow: hidden;
        --swiper-pagination-progressbar-size: 3px;
        @media screen and (min-width: 1024px) {
          display: none !important;
        }
      }
      .mini-cart-recommendations__carousel .swiper-pagination-progressbar .swiper-pagination-progressbar-fill {
        background-color: var(--nova-color-grey-600);
      }
      .mini-cart-recommendations__carousel-navigation {
        display: flex;
        align-items: center;
        gap: var(--nova-space-xsm);
      }
      @media screen and (max-width: 1023px) {
        .mini-cart-recommendations__carousel-navigation {
          display: none;
        }
      }
      .carousel--icon-container {
        width: 21px;
        height: 21px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 100%;
        background-color: transparent;
        color: var(--nova-color-grey-600);
        cursor: pointer;
        transition: opacity 0.2s;
        border: none;
        padding: 0;
        margin: 0;
        font: inherit;

        &.swiper-button-disabled {
          opacity: 0.3;
          pointer-events: none;
        }
      }
      .mini-cart-recommendation__add-to-cart {
        background-color: var(--nova-color-white);
        border: 1px solid var(--nova-color-grey-600);
        border-radius: 100%;
        cursor: pointer;
        padding: 0;
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
      }
      .mini-cart-recommendation__error {
        font: var(--nova-body-caption);
        letter-spacing: var(--nova-caption-letter-spacing);
        color: var(--nova-color-error, #b42318);
        margin: 0;
      }
    `,
  ]
}

customElements.define('product-complementary-products-list', ProductComplementaryProductsList)
